import React, { useContext, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Row, Col, Card, Button, Table } from 'reactstrap';
import Swal from 'sweetalert2';
import { ButtonLoader } from '../../../components/Loader.js';
import Pagination from '../../../components/Pagination.js';
import { UserContext } from '../../../contexts/UserContext.js';
import { hasRolePermission } from '../../../utility-services/rolePermissions.js';
import { SelectedFacilitiesContext } from '../../../contexts/SelectedFacilitiesContext.js';
import useSavedDrugQuotes from '../../../services/drugQuotes/useSavedDrugQuotes.js';
import useDeleteDrugQuote from '../../../services/drugQuotes/useDeleteDrugQuote.js';
import useRecalculateDrugQuote from '../../../services/drugQuotes/useRecalculateDrugQuote.js';
import { MdOutlineDeleteForever } from 'react-icons/md';
import { MdEdit } from 'react-icons/md';

function normalizeSavedQuotesList(raw) {
    let list;
    if (Array.isArray(raw)) list = raw;
    else if (raw == null) return [];
    else {
        list =
            raw?.result ??
            raw?.items ??
            raw?.quotes ??
            raw?.Quotes ??
            raw?.data?.result ??
            raw?.data?.items ??
            raw?.data?.quotes ??
            raw?.data?.Quotes ??
            raw?.data ??
            [];
    }
    if (!Array.isArray(list)) return [];
    return list.map(normalizeSavedQuoteRow);
}

/** Unwrap nested quote objects so id + fields live on one row. */
function normalizeSavedQuoteRow(item) {
    if (item == null || typeof item !== 'object') return item;
    let row = { ...item };
    const wrappers = [
        'drugQuote',
        'DrugQuote',
        'savedQuote',
        'SavedQuote',
        'quote',
        'Quote',
        'data',
        'Data',
        'result',
        'Result',
    ];
    for (const key of wrappers) {
        const inner = row[key];
        if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
            const { [key]: _removed, ...rest } = row;
            row = { ...rest, ...inner };
        }
    }
    return row;
}

function pick(obj, keys, fallback = '-') {
    if (obj == null || typeof obj !== 'object') return fallback;
    for (const k of keys) {
        if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
        const v = obj[k];
        if (v !== undefined && v !== null && v !== '') return v;
        if (v === 0) return 0;
    }
    return fallback;
}

const formatDecimalDisplay = (v) => {
    if (v === '' || v == null) return '-';
    const n = Number(v);
    if (!Number.isFinite(n)) return v;
    return String(Math.round(n * 100) / 100);
};

function isValidQuoteId(v) {
    if (v === undefined || v === null || v === '') return false;
    if (typeof v === 'number' && Number.isFinite(v)) return true;
    if (typeof v === 'string' && v.trim() !== '') return true;
    return false;
}

function getQuoteId(item) {
    if (item == null || typeof item !== 'object') return null;

    const directKeys = [
        'id',
        'Id',
        'ID',
        'quoteId',
        'QuoteId',
        'quoteID',
        'QuoteID',
        'drugQuoteId',
        'DrugQuoteId',
        'drugQuoteID',
        'DrugQuoteID',
        'savedQuoteId',
        'SavedQuoteId',
        'savedDrugQuoteId',
        'SavedDrugQuoteId',
        'lineId',
        'LineId',
        'recordId',
        'RecordId',
    ];
    for (const k of directKeys) {
        if (Object.prototype.hasOwnProperty.call(item, k) && isValidQuoteId(item[k])) {
            return item[k];
        }
    }

    for (const key of Object.keys(item)) {
        const norm = key.toLowerCase().replace(/[_-]/g, '');
        if (['facid', 'pharmid', 'userid', 'ndc'].includes(norm)) continue;
        const isQuoteIdKey =
            norm === 'id' ||
            norm === 'quoteid' ||
            norm === 'drugquoteid' ||
            norm === 'savedquoteid' ||
            norm === 'saveddrugquoteid' ||
            norm === 'lineid' ||
            norm === 'recordid' ||
            (norm.includes('quote') && norm.endsWith('id'));
        if (isQuoteIdKey) {
            const v = item[key];
            if (isValidQuoteId(v)) return v;
        }
    }

    const dq = item.drugQuotes ?? item.DrugQuotes;
    if (Array.isArray(dq) && dq?.length > 0) {
        const nested = getQuoteId(dq[0]);
        if (nested != null) return nested;
    }

    return null;
}

function pickField(item, keys) {
    const v = pick(item, keys, '');
    return v === '-' ? '' : String(v ?? '');
}

function normalizeTypeForForm(raw) {
    const s = String(raw ?? '')
        .trim()
        .toLowerCase();
    if (s === 'drug' || s === 'drugs') return 'drug';
    if (s === 'compound' || s === 'compounds') return 'compound';
    if (s === 'iv' || s === 'i.v.' || s === 'i.v' || s === 'i v') return 'iv';
    return s;
}

function buildEditStateFromItem(item, quoteId) {
    const quotesDescription = pickField(item, [
        'quotesDescription',
        'QuotesDescription',
        'drugdescription',
        'Drugdescription',
    ]);
    const drugName = pickField(item, ['drugName', 'DrugName']);
    const searchDrug = pickField(item, ['searchDrug', 'SearchDrug', 'searchDrugName', 'SearchDrugName']);
    const facId = pickField(item, ['facid', 'facID', 'FacID', 'facId']);
    const strength = pickField(item, ['strength', 'Strength']);
    const type = normalizeTypeForForm(pickField(item, ['type', 'Type', 'drugtype', 'Drugtype']));
    const ndc = pickField(item, ['ndc', 'NDC']);
    const quantity = pickField(item, ['quantity', 'qty', 'Qty', 'QTY']);
    const daysSupply = pickField(item, ['daysSupply', 'daysupply', 'Daysupply', 'DaysSupply']);
    const cost = pick(item, ['cost', 'Cost', 'price', 'Price', 'finalTotalPrice', 'FinalTotalPrice']);
    const pricePerDay = pick(item, ['pricePerDay', 'PricePerDay', 'pricePerday', 'PricePerday']);
    const items = item?.items ?? item?.drugQuotes ?? item?.DrugQuotes ?? [];
    return {
        editQuoteId: quoteId,
        facId,
        form: { quotesDescription, type, searchDrug, quantity, daysSupply },
        items,
        itemsCount: Array.isArray(items) ? items.length : 0,
        quoteLineItem: {
            id: `edit-${quoteId}`,
            payload: {
                Type: type,
                NDC: ndc,
                Qty: quantity,
                Daysupply: daysSupply,
                DrugName: drugName,
                searchDrug,
                FacID: facId,
                facID: facId,
            },
            response: {},
            finalTotalPriceSnapshot: cost === '-' ? '' : cost,
            pricePerDaySnapshot: pricePerDay === '-' ? '' : pricePerDay,
            strengthSnapshot: strength,
            drugNameSnapshot: drugName,
            searchDrug,
            quantitySnapshot: quantity,
            daysSupplySnapshot: daysSupply,
        },
    };
}

function SavedDrugQuotes() {
    const history = useHistory();
    const { user, roles } = useContext(UserContext);
    const canEditDrugQuote = hasRolePermission(roles, 'canEditDrugQuote');
    const canDeleteDrugQuote = hasRolePermission(roles, 'canDeleteDrugQuote');
    const { selectedFacilities } = useContext(SelectedFacilitiesContext);

    const facilityName = useMemo(() => {
        if (selectedFacilities?.length > 0) {
            return selectedFacilities.map((f) => f?.facID).filter((id) => id != null && id !== '');
        }
        return [];
    }, [selectedFacilities]);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const detailRef = React.useRef(null);

    const { savedDrugQuotesRaw, isSavedDrugQuotesLoading, refetchSavedDrugQuotes } = useSavedDrugQuotes(
        facilityName,
        pageNumber,
        pageSize
    );
    const { deleteDrugQuote, isDeleteDrugQuoteLoading } = useDeleteDrugQuote();
    const { recalculateDrugQuote, isRecalculateDrugQuoteLoading } = useRecalculateDrugQuote();

    const [deletingQuoteId, setDeletingQuoteId] = useState(null);
    const [recalculatingQuoteId, setRecalculatingQuoteId] = useState(null);

    const rows = useMemo(() => {
        const list = normalizeSavedQuotesList(savedDrugQuotesRaw);
        return Array.isArray(list) ? list : [];
    }, [savedDrugQuotesRaw]);

    React.useEffect(() => {
        const raw = savedDrugQuotesRaw;
        if (!raw) {
            setTotalCount(0);
            setTotalPages(1);
            return;
        }
        const items =
            raw?.items ??
            raw?.result ??
            raw?.data?.items ??
            raw?.data?.result ??
            (Array.isArray(raw) ? raw : null) ??
            [];
        const tc = Number(
            raw?.totalCount ?? raw?.total ?? raw?.data?.totalCount ?? (Array.isArray(items) ? items.length : 0)
        );
        const pn = Number(raw?.pageNumber ?? raw?.page ?? pageNumber);
        const ps = Number(raw?.pageSize ?? raw?.page_size ?? pageSize);
        const tp = Number(raw?.totalPages ?? raw?.total_pages ?? Math.max(1, Math.ceil(tc / (ps || pageSize))));
        setTotalCount(Number.isFinite(tc) ? tc : Array.isArray(items) ? items.length : 0);
        setTotalPages(Number.isFinite(tp) ? tp : 1);
        setPageNumber(Number.isFinite(pn) ? pn : pageNumber);
        setPageSize(Number.isFinite(ps) ? ps : pageSize);
    }, [savedDrugQuotesRaw]);

    const handleEditQuote = (item) => {
        if (!canEditDrugQuote) return;
        const quoteId = getQuoteId(item);
        if (quoteId == null) return;
        history.push({
            pathname: '/utilities/drug-quotes',
            state: buildEditStateFromItem(item, quoteId),
        });
    };

    React.useEffect(() => {
        if (selectedQuote && detailRef.current) {
            detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [selectedQuote]);

    const getSelectedQuoteId = () => getQuoteId(selectedQuote);

    const selectedQuoteItems = selectedQuote?.items ||
        selectedQuote?.drugQuotes ||
        selectedQuote?.DrugQuotes || [selectedQuote];

    const selectedQuotePricePerDayTotal = selectedQuoteItems.reduce((sum, drug) => {
        const value = pick(drug, ['pricePerDay', 'PricePerDay', 'pricePerday', 'PricePerday'], 0);
        const numberValue = Number(value);
        return sum + (Number.isFinite(numberValue) ? numberValue : 0);
    }, 0);

    const handleRecalculateQuote = (item) => {
        if (!canEditDrugQuote) return;
        const quoteId = getQuoteId(item);
        if (quoteId == null) return;
        setRecalculatingQuoteId(quoteId);
        recalculateDrugQuote(quoteId, {
            onSettled: () => setRecalculatingQuoteId(null),
            onSuccess: async (data) => {
                const responseQuote = normalizeSavedQuoteRow(data?.data ?? data?.result ?? data);
                const refetchResult = await refetchSavedDrugQuotes();
                const list = normalizeSavedQuotesList(refetchResult?.data ?? savedDrugQuotesRaw);
                const updatedFromList = list.find((q) => getQuoteId(q) === quoteId);
                setSelectedQuote(updatedFromList ?? responseQuote ?? item);
            },
        });
    };

    const handleDeleteQuote = (item) => {
        if (!canDeleteDrugQuote) return;
        const quoteId = getQuoteId(item);
        if (quoteId == null) return;
        Swal.fire({
            title: 'Delete saved quote?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: 'Cancel',
            confirmButtonText: 'Delete',
            buttonsStyling: false,
            reverseButtons: true,

            customClass: {
                actions: 'swal-actions-saved-drug-quotes',
                confirmButton: 'custom-green-button',
                cancelButton: 'custom-cancel-button',
            },
        }).then((result) => {
            if (!result.isConfirmed) return;
            setDeletingQuoteId(quoteId);
            deleteDrugQuote(quoteId, {
                onSettled: () => setDeletingQuoteId(null),
                onSuccess: () => {
                    setSelectedQuote(null);
                    refetchSavedDrugQuotes();
                },
            });
        });
    };

    const typeMap = {
        r: 'Drug',
        c: 'Compound',
        i: 'IV',
    };

    return (
        <div className="page-content">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="mb-0">Saved drug quotes</h2>
                <div className="d-flex align-items-center">
                    <Button
                        style={{ marginTop: '10px' }}
                        color="secondary"
                        outline
                        tag={Link}
                        to="/utilities/drug-quotes">
                        Back to drug quotes
                    </Button>
                    {/* <Button
                        color="primary"
                        className="ml-2"
                        onClick={() => refetchSavedDrugQuotes()}
                        disabled={!userId}>
                        Refresh
                    </Button> */}
                </div>
            </div>
            <Card className="p-2">
                <Row>
                    <Col md={12}>
                        {isSavedDrugQuotesLoading ? (
                            <div className="text-center p-5">
                                <ButtonLoader />
                            </div>
                        ) : (
                            <>
                                <Table className="table-bordered" responsive>
                                    <thead className="thead-light">
                                        <tr>
                                            <th>Description</th>
                                            <th>Facility ID</th>
                                            <th>Last Modified</th>
                                            <th>Price / Day</th>
                                            <th>Count</th>
                                            <th>Created By</th>
                                            <th>Updated By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows?.length > 0 ? (
                                            rows.map((item, index) => {
                                                const quoteId = getQuoteId(item);
                                                const selectedId = getSelectedQuoteId();
                                                const isSelected =
                                                    selectedId != null && quoteId != null && selectedId === quoteId;

                                                return (
                                                    <tr
                                                        key={quoteId ?? index}
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: isSelected ? '#e8f0ff' : undefined,
                                                        }}
                                                        onClick={() => setSelectedQuote(isSelected ? null : item)}>
                                                        <td>{item?.quotesDescription || '-'}</td>
                                                        <td>{item?.facid || '-'}</td>
                                                        <td>
                                                            {item?.lastModified
                                                                ? new Date(item.lastModified).toLocaleString('en-US', {
                                                                      month: '2-digit',
                                                                      day: '2-digit',
                                                                      year: 'numeric',
                                                                      hour: '2-digit',
                                                                      minute: '2-digit',
                                                                      second: '2-digit',
                                                                      hour12: true,
                                                                  })
                                                                : '-'}
                                                        </td>
                                                        <td>${formatDecimalDisplay(pick(item, ['pricePerDay']))}</td>
                                                        <td>
                                                            {item?.itemsCount ??
                                                                (Array.isArray(item?.items) ? item.items.length : '-')}
                                                        </td>
                                                        <td>{item?.createdByName || '-'}</td>
                                                        <td>{item?.updatedByName || '-'}</td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center p-4">
                                                    No saved drug quotes found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                                <div className="mt-2">
                                    <Pagination
                                        pageNumber={pageNumber}
                                        pageSize={pageSize}
                                        totalCount={totalCount}
                                        totalPages={totalPages}
                                        onPageChange={(p) => setPageNumber(p)}
                                    />
                                </div>
                            </>
                        )}
                    </Col>
                </Row>
            </Card>
            {selectedQuote && (
                <Card className="p-2 mt-3" innerRef={detailRef}>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <h1>
                                {selectedQuote?.quotesDescription
                                    ? selectedQuote.quotesDescription.charAt(0).toUpperCase() +
                                      selectedQuote.quotesDescription.slice(1)
                                    : ''}
                            </h1>
                            <div>
                                <strong>Facility:</strong> {selectedQuote?.facid || '-'}
                            </div>
                            <div>
                                <strong>Last Modified:</strong>{' '}
                                {selectedQuote?.lastModified
                                    ? new Date(selectedQuote.lastModified).toLocaleString('en-US', {
                                          month: '2-digit',
                                          day: '2-digit',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                          second: '2-digit',
                                          hour12: true,
                                      })
                                    : '-'}
                            </div>
                            {selectedQuote?.updatedByName && (
                                <>
                                    <strong>Modified by:</strong> {selectedQuote.updatedByName}
                                </>
                            )}{' '}
                            <div>
                                <h4 className="">${formatDecimalDisplay(selectedQuote?.pricePerDay)} / Day:</h4>
                            </div>
                            {selectedQuoteItems.some((item) => Boolean(pick(item, ['perDiem'], false))) && (
                                <div className=" bg-black" style={{ fontWeight: '600' }}>
                                    Per Diem Facility
                                </div>
                            )}
                        </div>
                    </div>

                    <Table bordered responsive>
                        <thead className="thead-light">
                            <tr>
                                <th>Drug Name</th>
                                <th>NDC</th>
                                <th>Type</th>
                                <th>Qty</th>
                                <th>Days Supply</th>
                                <th>
                                    {selectedQuote?.items?.some((item) => item.isIncluded === true)
                                        ? 'Excluded Cost'
                                        : 'Cost'}
                                </th>

                                <th>Price / Day</th>
                            </tr>
                        </thead>

                        <tbody>
                            {(
                                selectedQuote?.items ||
                                selectedQuote?.drugQuotes ||
                                selectedQuote?.DrugQuotes || [selectedQuote]
                            ).map((drug, idx) => {
                                return (
                                    <tr key={idx}>
                                        <td>{pick(drug, ['drugName', 'DrugName'])}</td>

                                        <td>{pick(drug, ['ndc', 'NDC'])}</td>
                                        <td>
                                            {typeMap[
                                                pick(drug, ['type', 'Type', 'drugtype', 'Drugtype'])?.toLowerCase()
                                            ] || '-'}
                                        </td>

                                        <td>{pick(drug, ['qty', 'quantity'])}</td>

                                        <td>{pick(drug, ['daysupply', 'daysSupply', 'Daysupply', 'DaysSupply'])}</td>

                                        <td>${formatDecimalDisplay(pick(drug, ['cost', 'Cost', 'price', 'Price']))}</td>

<td>
    {drug?.isIncluded === true
        ? 'Included in Daily Rate'
        : `$${formatDecimalDisplay(
              pick(drug, ['pricePerDay', 'PricePerDay'])
          )}`}
</td>                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>

                    <div className="d-flex justify-content-end">
                        <Button
                            size="sm"
                            style={{
                                boxShadow: 'none',
                                borderRadius: '5px',
                                padding: '6px 14px',
                                outline: 'none',
                                border: '1px solid #6565ff',
                                color: 'blue',
                                backgroundColor: 'transparent',
                                cursor:
                                    isRecalculateDrugQuoteLoading && recalculatingQuoteId === getSelectedQuoteId()
                                        ? 'default'
                                        : 'pointer',
                                marginRight: '10px',
                                fontWeight: '600',
                                opacity:
                                    isRecalculateDrugQuoteLoading && recalculatingQuoteId === getSelectedQuoteId()
                                        ? 0.6
                                        : 1,
                            }}
                            onClick={() => handleRecalculateQuote(selectedQuote)}
                            disabled={(isRecalculateDrugQuoteLoading && recalculatingQuoteId === getSelectedQuoteId()) || !canEditDrugQuote}>
                            {isRecalculateDrugQuoteLoading && recalculatingQuoteId === getSelectedQuoteId() ? (
                                <ButtonLoader />
                            ) : (
                                'Recalculate'
                            )}
                        </Button>
                        <Button size="sm" color="primary" onClick={() => handleEditQuote(selectedQuote)} disabled={!canEditDrugQuote}>
                            Edit
                        </Button>
                        <Button
                            size="sm"
                            color="danger"
                            className="ml-2"
                            onClick={() => handleDeleteQuote(selectedQuote)}
                            disabled={(isDeleteDrugQuoteLoading && deletingQuoteId === getSelectedQuoteId()) || !canDeleteDrugQuote}>
                            {isDeleteDrugQuoteLoading && deletingQuoteId === getSelectedQuoteId() ? (
                                <ButtonLoader />
                            ) : (
                                'Delete'
                            )}
                        </Button>
                    </div>
                </Card>
            )}
        </div>
    );
}

export default SavedDrugQuotes;
